import './UserMediaError.css';
import React, { useMemo } from 'react';
import { Button } from '../../shadcn/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../shadcn/accordion';

/** Best-effort browser family for tailored copy (not used for security). */
function detectBrowserFamily() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/Edg\//i.test(ua)) return 'edge';
  if (/Firefox\//i.test(ua)) return 'firefox';
  if (/Safari/i.test(ua) && !/Chrome|CriOS|Android/i.test(ua)) return 'safari';
  if (/Chrome|CriOS/i.test(ua)) return 'chrome';
  return 'other';
}

function defaultAccordionValue(family) {
  if (family === 'firefox') return 'firefox';
  if (family === 'safari') return 'safari';
  return 'chrome-edge';
}

export default function UserMediaError() {
  const browserFamily = useMemo(() => detectBrowserFamily(), []);
  const accordionDefault = useMemo(
    () => defaultAccordionValue(browserFamily),
    [browserFamily],
  );

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="user-media-error-page" role="alert">
      <div className="user-media-error-card">
        <h1 className="user-media-error-title">Camera or microphone blocked</h1>
        <p className="user-media-error-lead">
          Your browser won&apos;t let this lesson use your camera or mic until you allow them
          for this site. After you change the setting, use <strong>Retry</strong> below — the
          browser usually won&apos;t show the prompt again until access is allowed.
        </p>

        <ol className="user-media-error-steps">
          <li>
            Find the <strong>lock</strong>, <strong>camera</strong>, or <strong>site info</strong>{' '}
            icon in the address bar (left of the web address).
          </li>
          <li>
            Open <strong>site settings</strong> or <strong>permissions</strong> for this site.
          </li>
          <li>
            Set <strong>Camera</strong> and <strong>Microphone</strong> to{' '}
            <strong>Allow</strong> (not Block).
          </li>
          <li>
            If you don&apos;t see those options, check <strong>Windows / Mac system privacy</strong>{' '}
            settings — sometimes the OS blocks the browser entirely.
          </li>
        </ol>

        <Accordion
          type="single"
          collapsible
          defaultValue={accordionDefault}
          className="user-media-error-accordion"
        >
          <AccordionItem value="chrome-edge">
            <AccordionTrigger className="user-media-error-accordion-trigger">
              Chrome or Edge (desktop)
            </AccordionTrigger>
            <AccordionContent>
              <ul className="user-media-error-list">
                <li>Click the lock or tune icon in the address bar.</li>
                <li>
                  Choose <strong>Site settings</strong> or{' '}
                  <strong>Permissions for this site</strong>.
                </li>
                <li>Under Camera and Microphone, select <strong>Allow</strong>.</li>
                <li>Return here and click <strong>Retry</strong>.</li>
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="safari">
            <AccordionTrigger className="user-media-error-accordion-trigger">
              Safari (Mac or iPhone / iPad)
            </AccordionTrigger>
            <AccordionContent>
              <p className="user-media-error-note">
                <strong>Mac:</strong> Safari → <strong>Settings for This Website</strong> (or use
                the site settings in the address bar), then set Camera and Microphone to{' '}
                <strong>Allow</strong>.
              </p>
              <p className="user-media-error-note">
                <strong>iPhone / iPad:</strong> open the <strong>Settings</strong> app →{' '}
                <strong>Safari</strong> → <strong>Camera</strong> / <strong>Microphone</strong> (or
                find this site under <strong>Privacy &amp; Security</strong>) and allow access,
                then return and tap <strong>Retry</strong>.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="firefox">
            <AccordionTrigger className="user-media-error-accordion-trigger">
              Firefox
            </AccordionTrigger>
            <AccordionContent>
              <ul className="user-media-error-list">
                <li>Click the lock icon in the address bar.</li>
                <li>
                  Clear blocked permissions or set Camera / Microphone to <strong>Allow</strong>.
                </li>
                <li>Return here and click <strong>Retry</strong>.</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="user-media-error-actions">
          <Button type="button" size="lg" onClick={handleRetry}>
            Retry after allowing access
          </Button>
          <a
            className="user-media-error-doc-link"
            href="https://docs.daily.co/guides/how-daily-works/handling-device-permissions"
            target="_blank"
            rel="noopener noreferrer">
            More help from Daily (opens in new tab)
          </a>
        </div>
      </div>
    </div>
  );
}
